import { useLocalization } from "@/context/localization-context";
import type { TranslationKey } from "@/i18n/messages";
import { EmptyBlock, SectionCard, StatePill } from "../../components/primitives";
import type { ModelInfo } from "../../types";
import { FactList, SubHeading } from "./stat-tile";

const KNOWN_MODELS = ["burnt_cnn", "blur_cnn", "obstacle_seg", "slicing_cnn"];

/** The ONNX models of this process (loaded lazily on first use, so "not loaded" is no problem). */
export function ModelsCard({ models }: { models: ModelInfo[] }) {
  const { t, formatNumber } = useLocalization();
  return (
    <SectionCard title={t("monitoring.backend.models.title")} description={t("monitoring.backend.models.description")}>
      {models.length === 0 ? (
        <EmptyBlock>{t("monitoring.backend.models.empty")}</EmptyBlock>
      ) : (
        <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 14rem), 1fr))" }}>
          {models.map((model) => (
            <div key={model.name} className="flex min-w-0 flex-col gap-2 rounded-md border border-border/70 px-3 py-2">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-medium">
                    {KNOWN_MODELS.includes(model.name)
                      ? t(`monitoring.backend.models.name.${model.name}` as TranslationKey)
                      : model.name}
                  </div>
                  <div className="break-all font-mono text-[11px] text-muted-foreground">{model.name}</div>
                </div>
                <StatePill tone={model.loaded ? "ok" : "neutral"}>
                  {model.loaded ? t("monitoring.backend.models.loaded") : t("monitoring.backend.models.notLoaded")}
                </StatePill>
              </div>
              <FactList
                min="100%"
                items={[
                  { label: t("monitoring.backend.models.version"), value: <span className="break-all">{model.version || "–"}</span> },
                  { label: t("monitoring.backend.models.threads"), value: model.threads ?? "–" },
                  {
                    label: t("monitoring.backend.models.threshold"),
                    value: model.threshold === null ? "–" : formatNumber(model.threshold, { maximumFractionDigits: 4 }),
                  },
                ]}
              />
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
}

const isGroup = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/** One setting value: booleans as on / off, null as a dash, long strings wrapped. */
function SettingValue({ value }: { value: unknown }) {
  const { t, formatNumber } = useLocalization();
  if (value === null || value === undefined || value === "") return <span className="text-muted-foreground">–</span>;
  if (typeof value === "boolean") return <>{value ? t("monitoring.backend.on") : t("monitoring.backend.off")}</>;
  if (typeof value === "number") return <>{formatNumber(value, { maximumFractionDigits: 4 })}</>;
  if (typeof value === "string") return <span className="break-all">{value}</span>;
  if (Array.isArray(value)) return <span className="break-all">{value.map(String).join(", ") || "–"}</span>;
  return <span className="break-all">{JSON.stringify(value)}</span>;
}

/** The settings snapshot, grouped by its nested objects (top-level scalars form the "general" group). */
export function SettingsCard({ settings }: { settings: Record<string, unknown> }) {
  const { t } = useLocalization();
  const entries = Object.entries(settings ?? {});
  const general = entries.filter(([, value]) => !isGroup(value));
  const groups = entries.filter((entry): entry is [string, Record<string, unknown>] => isGroup(entry[1]));
  const all: { name: string; label: string; config: boolean; items: [string, unknown][] }[] = [
    ...(general.length
      ? [{ name: "__general", label: t("monitoring.backend.settings.general"), config: false, items: general }]
      : []),
    ...groups.map(([name, value]) => ({ name, label: name, config: true, items: Object.entries(value) })),
  ];

  return (
    <SectionCard title={t("monitoring.backend.settings.title")} description={t("monitoring.backend.settings.description")}>
      {all.length === 0 ? (
        <EmptyBlock>{t("monitoring.backend.settings.empty")}</EmptyBlock>
      ) : (
        <div className="grid gap-x-6 gap-y-5" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 18rem), 1fr))" }}>
          {all.map(({ name, label, config, items }) => (
            <div key={name} className="flex min-w-0 flex-col gap-1">
              <SubHeading>
                {config ? <span className="break-all font-mono normal-case tracking-normal">{label}</span> : label}
              </SubHeading>
              <FactList
                min="100%"
                items={items.map(([key, value]) => ({ label: <span className="font-mono">{key}</span>, value: <SettingValue value={value} /> }))}
              />
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
}
